/**
 * Customer Account API auth (Stage 1 of docs/V1_TO_V2_MIGRATION.md).
 *
 * - Liquid `customer` (config.customerId) decides whether the customer is logged in.
 *   The CA API token is only a credential for API calls.
 * - Sign-in always goes through the storefront login, never the headless authorize endpoint.
 *   It returns to the callback page, which gets a token before going on to the original page.
 * - Tokens are obtained with a silent (`prompt=none`) authorize redirect, which works
 *   when a storefront session exists. `login_required` means "not logged in".
 *
 * Config comes from snippets/filatori-auth.liquid (#FilatoriAuthConfig).
 * Public API: window.FilatoriAuth
 */
(function () {
  const SCOPE = 'openid email customer-account-api:full';
  const TOKENS_KEY = 'filatori_auth_tokens';
  const PENDING_KEY = 'filatori_auth_pending';
  const LOGIN_REQUIRED_KEY = 'filatori_auth_login_required_at';
  const DISCOVERY_KEY = 'filatori_auth_discovery';
  // Treat the token as expired this long before it actually expires
  const EXPIRY_BUFFER_MS = 60 * 1000;
  // An authorize round trip older than this is rejected
  const PENDING_MAX_AGE_MS = 10 * 60 * 1000;
  // After `login_required`, don't redirect again for this long (avoids redirect loops)
  const LOGIN_REQUIRED_TTL_MS = 5 * 60 * 1000;

  const config = readConfig();
  let refreshPromise = null;
  let discoveryPromise = null;

  syncWithLiquidCustomer();

  window.FilatoriAuth = {
    isLoggedIn,
    getToken,
    request,
    signIn,
    getSignInUrl,
    logout,
    clearTokens,
    handleCallback,
  };

  function readConfig() {
    const el = document.querySelector('#FilatoriAuthConfig');
    if (!el) {
      throw new Error('FilatoriAuth: #FilatoriAuthConfig is missing');
    }

    return JSON.parse(el.textContent);
  }

  function isLoggedIn() {
    return Boolean(config.customerId);
  }

  // Tokens belong to one Liquid customer. Drop them after a logout or a customer switch.
  function syncWithLiquidCustomer() {
    const tokens = readTokens();
    if (!tokens) {
      return;
    }

    if (!isLoggedIn() || String(tokens.customerId) !== String(config.customerId)) {
      clearTokens();
    }
  }

  /**
   * Resolves to an access token, or null when the customer is not logged in.
   * When a new token is needed, the page redirects to a silent authorize and comes
   * back to `returnTo` (default: the current page). The promise then never resolves,
   * so callers must persist any unsaved work before calling this.
   */
  async function getToken(returnTo) {
    if (!isLoggedIn()) {
      return null;
    }

    const tokens = readTokens();
    if (tokens && !isExpired(tokens)) {
      return tokens.accessToken;
    }

    if (tokens && tokens.refreshToken) {
      const refreshed = await refreshTokens(tokens).catch(function onRefreshError(error) {
        console.warn('FilatoriAuth: refresh failed', error);
        return null;
      });
      if (refreshed) {
        return refreshed.accessToken;
      }
    }

    if (hasRecentLoginRequired()) {
      return null;
    }

    await authorizeSilently(returnTo);

    return new Promise(function waitForRedirect() {});
  }

  function isExpired(tokens) {
    return Date.now() >= tokens.expiresAt - EXPIRY_BUFFER_MS;
  }

  function refreshTokens(tokens) {
    if (!refreshPromise) {
      refreshPromise = doRefresh(tokens).finally(function onRefreshDone() {
        refreshPromise = null;
      });
    }

    return refreshPromise;
  }

  async function doRefresh(tokens) {
    const discovery = await getDiscovery();

    const body = new URLSearchParams();
    body.append('grant_type', 'refresh_token');
    body.append('client_id', config.clientId);
    body.append('refresh_token', tokens.refreshToken);

    const data = await postToken(discovery.tokenEndpoint, body);

    return saveTokens(data, tokens);
  }

  async function authorizeSilently(returnTo) {
    const discovery = await getDiscovery();
    const verifier = randomString();
    const challenge = await generateChallenge(verifier);
    const state = randomString();

    writeJson(localStorage, PENDING_KEY, {
      state,
      verifier,
      returnTo: safeReturnPath(returnTo),
      createdAt: Date.now(),
    });

    const url = new URL(discovery.authorizationEndpoint);
    url.searchParams.append('scope', SCOPE);
    url.searchParams.append('client_id', config.clientId);
    url.searchParams.append('response_type', 'code');
    url.searchParams.append('redirect_uri', getRedirectUri());
    url.searchParams.append('state', state);
    url.searchParams.append('code_challenge', challenge);
    url.searchParams.append('code_challenge_method', 'S256');
    url.searchParams.append('prompt', 'none');
    if (config.locale) {
      url.searchParams.append('ui_locales', config.locale);
    }

    window.location.href = url.toString();
  }

  /**
   * Runs on the callback page, which is reached twice after a sign-in:
   * 1. From the storefront sign-in (`?return_to=`): gets a token with a silent authorize.
   * 2. From that authorize (`?code=&state=`): exchanges the code and stores the tokens.
   * Returns { ok, returnTo, error }. The caller redirects to `returnTo`.
   */
  async function handleCallback() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('state') && params.has('return_to')) {
      return handleSignInReturn(params.get('return_to'));
    }

    const pending = readJson(localStorage, PENDING_KEY);
    localStorage.removeItem(PENDING_KEY);

    const returnTo = pending ? pending.returnTo : '/';

    if (!pending) {
      return { ok: false, returnTo, error: 'missing_state' };
    }

    if (Date.now() - pending.createdAt > PENDING_MAX_AGE_MS) {
      return { ok: false, returnTo, error: 'expired' };
    }

    if (params.get('state') !== pending.state) {
      return { ok: false, returnTo, error: 'state_mismatch' };
    }

    const error = params.get('error');
    if (error) {
      clearTokens();
      if (error === 'login_required') {
        sessionStorage.setItem(LOGIN_REQUIRED_KEY, String(Date.now()));
      }

      return { ok: false, returnTo, error };
    }

    if (!isLoggedIn()) {
      return { ok: false, returnTo, error: 'no_storefront_session' };
    }

    const discovery = await getDiscovery();

    const body = new URLSearchParams();
    body.append('grant_type', 'authorization_code');
    body.append('client_id', config.clientId);
    body.append('redirect_uri', getRedirectUri());
    body.append('code', params.get('code') || '');
    body.append('code_verifier', pending.verifier);

    const data = await postToken(discovery.tokenEndpoint, body);
    saveTokens(data, null);
    sessionStorage.removeItem(LOGIN_REQUIRED_KEY);

    return { ok: true, returnTo };
  }

  // Right after the storefront sign-in there is never a token, so get it before the
  // customer reaches `path`. That page then never has to leave to get one.
  async function handleSignInReturn(path) {
    const returnTo = safeReturnPath(path);
    if (!isLoggedIn()) {
      return { ok: false, returnTo, error: 'no_storefront_session' };
    }

    // Redirects to the silent authorize, which comes back to this page with a code
    const token = await getToken(returnTo);
    if (!token) {
      return { ok: false, returnTo, error: 'login_required' };
    }

    return { ok: true, returnTo };
  }

  function hasRecentLoginRequired() {
    const failedAt = Number(sessionStorage.getItem(LOGIN_REQUIRED_KEY));
    if (!failedAt) {
      return false;
    }

    return Date.now() - failedAt < LOGIN_REQUIRED_TTL_MS;
  }

  async function postToken(tokenEndpoint, body) {
    const res = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body,
    });

    const data = await res.json().catch(function onInvalidJson() {
      return {};
    });

    if (!res.ok || data.error || !data.access_token) {
      throw new Error(`FilatoriAuth: token request failed (${res.status}) ${data.error || ''}`);
    }

    return data;
  }

  function saveTokens(data, previous) {
    const tokens = {
      accessToken: data.access_token,
      // A refresh response may not include a new refresh or id token
      refreshToken: data.refresh_token || (previous ? previous.refreshToken : null),
      idToken: data.id_token || (previous ? previous.idToken : null),
      expiresAt: Date.now() + data.expires_in * 1000,
      customerId: config.customerId,
    };

    writeJson(localStorage, TOKENS_KEY, tokens);

    return tokens;
  }

  function readTokens() {
    return readJson(localStorage, TOKENS_KEY);
  }

  function clearTokens() {
    localStorage.removeItem(TOKENS_KEY);
  }

  /**
   * Customer Account API GraphQL call. Resolves to `data`, or null when the customer
   * is not logged in. Throws on HTTP or GraphQL errors. Mutation `userErrors` are
   * left to the caller.
   */
  async function request(query, variables, returnTo) {
    const token = await getToken(returnTo);
    if (!token) {
      return null;
    }

    let res = await postGraphql(query, variables, token);

    // Token revoked or expired early: refresh once and retry
    if (res.status === 401) {
      const tokens = readTokens();
      if (tokens) {
        tokens.expiresAt = 0;
        writeJson(localStorage, TOKENS_KEY, tokens);
      }

      const newToken = await getToken(returnTo);
      if (!newToken) {
        return null;
      }

      res = await postGraphql(query, variables, newToken);
    }

    if (!res.ok) {
      throw new Error(`FilatoriAuth: API request failed (${res.status})`);
    }

    const body = await res.json();
    if (body.errors && body.errors.length) {
      throw new Error(`FilatoriAuth: ${body.errors.map(getMessage).join('; ')}`);
    }

    return body.data;
  }

  async function postGraphql(query, variables, token) {
    const discovery = await getDiscovery();

    return fetch(discovery.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify({ query, variables: variables || {} }),
    });
  }

  function getMessage(error) {
    return error.message;
  }

  // Sign-in returns to the callback page, which gets a token and then goes on to `returnTo`
  function getSignInUrl(returnTo) {
    const callbackUrl = `${config.callbackPath}?return_to=${encodeURIComponent(safeReturnPath(returnTo))}`;

    return `/customer_authentication/login?return_to=${encodeURIComponent(callbackUrl)}`;
  }

  function signIn(returnTo) {
    sessionStorage.removeItem(LOGIN_REQUIRED_KEY);
    window.location.href = getSignInUrl(returnTo);
  }

  function logout() {
    clearTokens();
    localStorage.removeItem(PENDING_KEY);
    sessionStorage.removeItem(LOGIN_REQUIRED_KEY);
    window.location.href = config.logoutUrl;
  }

  function getDiscovery() {
    if (!discoveryPromise) {
      discoveryPromise = loadDiscovery().catch(function onDiscoveryError(error) {
        discoveryPromise = null;
        throw error;
      });
    }

    return discoveryPromise;
  }

  async function loadDiscovery() {
    const cached = readJson(sessionStorage, DISCOVERY_KEY);
    if (cached) {
      return cached;
    }

    const [openIdRes, apiRes] = await Promise.all([
      fetch('/.well-known/openid-configuration'),
      fetch('/.well-known/customer-account-api'),
    ]);

    if (!openIdRes.ok || !apiRes.ok) {
      throw new Error(`FilatoriAuth: discovery failed (${openIdRes.status} / ${apiRes.status})`);
    }

    const openId = await openIdRes.json();
    const api = await apiRes.json();

    const discovery = {
      authorizationEndpoint: openId.authorization_endpoint,
      tokenEndpoint: openId.token_endpoint,
      graphqlEndpoint: api.graphql_api,
    };

    writeJson(sessionStorage, DISCOVERY_KEY, discovery);

    return discovery;
  }

  function getRedirectUri() {
    return `${window.location.origin}${config.callbackPath}`;
  }

  // Only same-site relative paths, so return_to can't be used as an open redirect
  function safeReturnPath(path) {
    const value = path || `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (value.charAt(0) !== '/' || value.charAt(1) === '/' || value.charAt(1) === '\\') {
      return '/';
    }

    return value;
  }

  function randomString() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);

    return base64UrlEncode(array);
  }

  async function generateChallenge(verifier) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));

    return base64UrlEncode(new Uint8Array(digest));
  }

  function base64UrlEncode(bytes) {
    const binary = String.fromCharCode.apply(null, Array.from(bytes));

    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  function readJson(storage, key) {
    try {
      return JSON.parse(storage.getItem(key));
    } catch (error) {
      return null;
    }
  }

  function writeJson(storage, key, value) {
    storage.setItem(key, JSON.stringify(value));
  }
})();
